/**
 * Maps API error `field` keys (snake_case) to Excel template column labels.
 */
const BULK_UPLOAD_FIELD_LABELS: Record<string, string> = {
  customer_name: "Customer Name",
  gst_treatment: "GST Treatment",
  gstin_vat_tax_id: "GSTIN",
  pan_tax_registration_no: "PAN",
  contact_name_1: "Primary Contact Name",
  contact_number_1: "Primary Contact Number",
  contact_email_1: "Primary Contact Email",
  contact_name_2: "Secondary Contact Name",
  contact_number_2: "Secondary Contact Number",
  contact_email_2: "Secondary Contact Email",
  billing_address: "Billing Address",
  shipping_address: "Shipping Address",
  city: "City",
  country: "Country",
  state: "State",
  postal_code: "Postal Code",
  credit_limit: "Credit Limit",
  payment_terms: "Payment Terms (Days)",
  currency: "Currency",
  status: "Status",
  vendor_legal_name: "Vendor Legal Name",
  vendor_legal_entity: "Vendor Legal Entity",
  type_of_vendor: "Type of Vendor",
  business_category: "Business Category",
  trading_name: "Trading Name",
  registered_office_address: "Registered Office Address",
  primary_contact_name: "Primary Contact Name",
  primary_contact_phone: "Primary Contact Phone",
  primary_contact_email: "Primary Contact Email",
  pan_tax_id: "PAN",
  gst_category: "GST Category",
  gstin: "GSTIN",
  msme_flag: "MSME Flag",
  udyam_number: "UDYAM Number",
  beneficiary_name: "Beneficiary Name",
  bank_account_number: "Bank Account Number",
  bank_name: "Bank Name",
  branch: "Branch",
  ifsc_swift_code: "IFSC/SWIFT Code",
  tds_applicable: "TDS Applicable",
  tds_tax_rate: "TDS Tax Rate",
  duplicate: "Duplicate row",
  zoho_creation: "Zoho Books",
  insert: "Database",
  embedding: "Search index",
  header: "File header",
  system: "System",
  account_type: "Account Type",
  account_name: "Account Name",
  is_sub_account: "Make This A Sub-Account",
  place_under_group: "Place Under Group",
  parent_gl_name: "Parent Group",
  node_kind: "Ledger or Group",
  description: "Description",
  use_in_invoices: "Use In Invoices",
  use_in_bills: "Use In Bills",
  usage: "Usage",
};

const WORD_OVERRIDES: Record<string, string> = {
  gst: "GST",
  gstin: "GSTIN",
  pan: "PAN",
  tds: "TDS",
  msme: "MSME",
  udyam: "UDYAM",
  ifsc: "IFSC",
  swift: "SWIFT",
  vat: "VAT",
  id: "ID",
  email: "Email",
  url: "URL",
};

function titleCaseWord(word: string): string {
  const lower = word.toLowerCase();
  if (WORD_OVERRIDES[lower]) {
    return WORD_OVERRIDES[lower];
  }
  if (/^\d+$/.test(word)) {
    return word;
  }
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

function snakeCaseToLabel(field: string): string {
  return field.split("_").filter(Boolean).map(titleCaseWord).join(" ");
}

/** Human-readable label for bulk-upload error `field` values from the API. */
export function formatBulkUploadFieldLabel(field: string | undefined): string {
  const key = (field ?? "").trim();
  if (!key) {
    return "General";
  }
  if (BULK_UPLOAD_FIELD_LABELS[key]) {
    return BULK_UPLOAD_FIELD_LABELS[key];
  }
  return snakeCaseToLabel(key);
}

const UPPERCASE_VALUE_COLUMNS = new Set(["city", "state", "country"]);

const SNAKE_CASE_VALUE_COLUMNS = new Set([
  "account_type",
  "gst_treatment",
  "gst_category",
  "type_of_vendor",
  "business_category",
  "msme_flag",
  "tds_applicable",
  "status",
]);

function formatEnumLikeCellValue(raw: string): string {
  const trimmed = raw.trim();
  if (trimmed.includes("_")) {
    return snakeCaseToLabel(trimmed);
  }
  return titleCaseWord(trimmed);
}

/** Display formatting for bulk-upload review table cell values. */
export function formatBulkUploadCellValue(
  columnKey: string,
  value: unknown,
): string {
  if (value == null) return "—";
  if (typeof value === "object") return JSON.stringify(value);
  const s = String(value).trim();
  if (s === "") return "—";

  const col = columnKey.toLowerCase();
  if (UPPERCASE_VALUE_COLUMNS.has(col)) {
    return s.toUpperCase();
  }
  if (SNAKE_CASE_VALUE_COLUMNS.has(col)) {
    return formatEnumLikeCellValue(s);
  }
  if (
    col === "use_in_invoices" ||
    col === "use_in_bills" ||
    col === "is_sub_account" ||
    col === "invoice" ||
    col === "bill"
  ) {
    if (value === true || s.toLowerCase() === "true" || s.toLowerCase() === "yes") {
      return "Yes";
    }
    if (value === false || s.toLowerCase() === "false" || s.toLowerCase() === "no") {
      return "No";
    }
  }
  return s;
}
